import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface ConfigureSubjectsProps {
  domain: string;
}

interface Subject {
  id: string;
  subjectName: string;
  subjectCode: string;
  className: string;
  totalMarks: number;
  passingMarks: number;
  subjectType: string;
  teacherName?: string;
  teacherId?: string;
  isActive: boolean;
  createdAt: string;
}

const ConfigureSubjects: React.FC<ConfigureSubjectsProps> = ({ domain }) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    subjectName: '',
    subjectCode: '',
    className: '1',
    totalMarks: 100,
    passingMarks: 33,
    subjectType: 'Core',
    teacherName: '',
    teacherId: '',
    isActive: true,
  });

  const subjectTypes = ['Core', 'Elective', 'Optional', 'Extra-Curricular'];

  useEffect(() => {
    loadSubjects();
  }, [domain]);

  const loadSubjects = async () => {
    setLoading(true);
    try {
      const subjectsRef = collection(db, `school-data/${domain}/subjects`);
      const snapshot = await getDocs(subjectsRef);

      const subjectsList: Subject[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Subject));

      // Sort by class and subject name
      subjectsList.sort((a, b) => {
        if (a.className !== b.className) {
          return parseInt(a.className) - parseInt(b.className);
        }
        return a.subjectName.localeCompare(b.subjectName);
      });

      setSubjects(subjectsList);
    } catch (error: any) {
      console.error('Error loading subjects:', error);
      alert(`Failed to load subjects: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const subjectId = `${formData.subjectCode.toLowerCase().replace(/\s+/g, '_')}_${formData.className}`;

      const subjectData = {
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await setDoc(doc(db, `school-data/${domain}/subjects`, subjectId), subjectData);

      alert('Subject configuration saved successfully!');
      setShowAddForm(false);
      setFormData({
        subjectName: '',
        subjectCode: '',
        className: '1',
        totalMarks: 100,
        passingMarks: 33,
        subjectType: 'Core',
        teacherName: '',
        teacherId: '',
        isActive: true,
      });
      loadSubjects();
    } catch (error: any) {
      console.error('Error saving subject:', error);
      alert(`Failed to save subject: ${error.message}`);
    }
  };

  const handleDelete = async (subjectId: string) => {
    if (!confirm('Are you sure you want to delete this subject configuration?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `school-data/${domain}/subjects`, subjectId));
      alert('Subject configuration deleted successfully!');
      loadSubjects();
    } catch (error: any) {
      console.error('Error deleting subject:', error);
      alert(`Failed to delete subject: ${error.message}`);
    }
  };

  const toggleActive = async (subject: Subject) => {
    try {
      await setDoc(
        doc(db, `school-data/${domain}/subjects`, subject.id),
        { ...subject, isActive: !subject.isActive, updatedAt: new Date().toISOString() }
      );
      loadSubjects();
    } catch (error: any) {
      console.error('Error updating subject:', error);
      alert(`Failed to update subject: ${error.message}`);
    }
  };

  const getFilteredSubjects = () => {
    if (!selectedClass) return subjects;
    return subjects.filter(s => s.className === selectedClass);
  };

  const downloadExcelTemplate = () => {
    const templateData = [
      {
        'Subject Name': 'Mathematics',
        'Subject Code': 'MATH101',
        'Class': '10',
        'Subject Type': 'Core',
        'Total Marks': 100,
        'Passing Marks': 33,
        'Teacher ID': '17PVSS0001',
        'Teacher Name': 'John Doe',
        'Active': 'Yes',
      },
      {
        'Subject Name': 'Science',
        'Subject Code': 'SCI101',
        'Class': '10',
        'Subject Type': 'Core',
        'Total Marks': 100,
        'Passing Marks': 33,
        'Teacher ID': '17PVSS0002',
        'Teacher Name': 'Jane Smith',
        'Active': 'Yes',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Subjects');

    XLSX.writeFile(wb, 'subjects_template.xlsx');
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        alert('No data found in Excel file!');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const row of jsonData) {
        try {
          const subjectCode = row['Subject Code'] || '';
          const className = String(row['Class'] || '');

          if (!subjectCode || !className) {
            errorCount++;
            continue;
          }

          const subjectId = `${subjectCode.toLowerCase().replace(/\s+/g, '_')}_${className}`;

          const subjectData = {
            subjectName: row['Subject Name'] || '',
            subjectCode,
            className,
            totalMarks: Number(row['Total Marks']) || 100,
            passingMarks: Number(row['Passing Marks']) || 33,
            subjectType: row['Subject Type'] || 'Core',
            teacherName: row['Teacher Name'] || '',
            teacherId: row['Teacher ID'] || '',
            isActive: String(row['Active']).toLowerCase() === 'yes' || String(row['Active']).toLowerCase() === 'true',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          await setDoc(doc(db, `school-data/${domain}/subjects`, subjectId), subjectData);
          successCount++;
        } catch (error) {
          console.error('Error processing row:', row, error);
          errorCount++;
        }
      }

      alert(
        `Excel upload complete!\n\nSuccess: ${successCount} subjects\nFailed: ${errorCount} subjects`
      );

      loadSubjects();

      // Reset file input
      event.target.value = '';
    } catch (error: any) {
      console.error('Error uploading Excel:', error);
      alert(`Failed to upload Excel file: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const filteredSubjects = getFilteredSubjects();

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-book me-2 text-primary-custom"></i>
              Configure Subjects
            </h5>
            <small className="text-muted">Set up subjects for each class</small>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {showAddForm ? 'Cancel' : 'Add Subject'}
          </button>
        </div>

        {/* Excel Upload/Download Buttons */}
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <button
              className="btn btn-outline-success btn-sm w-100"
              onClick={downloadExcelTemplate}
            >
              <i className="bi bi-download me-2"></i>
              Download Excel Template
            </button>
          </div>
          <div className="col-md-6">
            <label className="btn btn-outline-primary btn-sm w-100" style={{ cursor: 'pointer' }}>
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  Upload Excel (Bulk)
                </>
              )}
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {/* Filter by Class */}
        <div className="row mt-3">
          <div className="col-md-3">
            <select
              className="form-select form-select-sm"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  Class {i + 1}
                </option>
              ))}
            </select>
          </div>
          {selectedClass && (
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setSelectedClass('')}
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card-body">
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="card mb-4" style={{ backgroundColor: '#f8f4ff' }}>
            <div className="card-body">
              <h6 className="mb-3">
                <i className="bi bi-plus-square me-2"></i>
                Add New Subject Configuration
              </h6>
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Subject Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.subjectName}
                      onChange={(e) => handleInputChange('subjectName', e.target.value)}
                      placeholder="e.g., Mathematics"
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Subject Code *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.subjectCode}
                      onChange={(e) => handleInputChange('subjectCode', e.target.value)}
                      placeholder="e.g., MATH101"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Class *</label>
                    <select
                      className="form-select"
                      value={formData.className}
                      onChange={(e) => handleInputChange('className', e.target.value)}
                      required
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          Class {i + 1}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Subject Type *</label>
                    <select
                      className="form-select"
                      value={formData.subjectType}
                      onChange={(e) => handleInputChange('subjectType', e.target.value)}
                      required
                    >
                      {subjectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Total Marks *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.totalMarks}
                      onChange={(e) => handleInputChange('totalMarks', parseInt(e.target.value))}
                      min="1"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Passing Marks *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.passingMarks}
                      onChange={(e) => handleInputChange('passingMarks', parseInt(e.target.value))}
                      min="1"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Teacher ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.teacherId}
                      onChange={(e) => handleInputChange('teacherId', e.target.value)}
                      placeholder="e.g., 17PVSS0001"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Teacher Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.teacherName}
                      onChange={(e) => handleInputChange('teacherName', e.target.value)}
                      placeholder="e.g., John Doe"
                    />
                  </div>

                  <div className="col-md-12">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active (Available for results upload)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-save me-2"></i>
                    Save Subject Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Subjects List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading subjects...</p>
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-book-half display-1 text-muted"></i>
            <p className="mt-3 text-muted">
              {subjects.length === 0
                ? 'No subjects configured yet. Click "Add Subject" to get started.'
                : 'No subjects found for the selected class.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Subject Name</th>
                  <th>Code</th>
                  <th>Class</th>
                  <th>Type</th>
                  <th>Marks</th>
                  <th>Teacher</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubjects.map((subject) => (
                  <tr key={subject.id}>
                    <td>
                      <strong>{subject.subjectName}</strong>
                    </td>
                    <td>
                      <span className="badge bg-secondary">{subject.subjectCode}</span>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">Class {subject.className}</span>
                    </td>
                    <td>
                      <span className="badge bg-primary">{subject.subjectType}</span>
                    </td>
                    <td>
                      <small>
                        Total: {subject.totalMarks}
                        <br />
                        Pass: {subject.passingMarks}
                      </small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {subject.teacherName || 'Not assigned'}
                        {subject.teacherId && (
                          <>
                            <br />
                            ID: {subject.teacherId}
                          </>
                        )}
                      </small>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${subject.isActive ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => toggleActive(subject)}
                      >
                        {subject.isActive ? (
                          <>
                            <i className="bi bi-check-circle me-1"></i>Active
                          </>
                        ) : (
                          <>
                            <i className="bi bi-x-circle me-1"></i>Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(subject.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Stats */}
        {!loading && filteredSubjects.length > 0 && (
          <div className="row mt-4">
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body text-center">
                  <h3 className="mb-0">{filteredSubjects.length}</h3>
                  <small className="text-muted">Total Subjects</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-success">
                    {filteredSubjects.filter(s => s.isActive).length}
                  </h3>
                  <small className="text-muted">Active</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-primary bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-primary">
                    {filteredSubjects.filter(s => s.subjectType === 'Core').length}
                  </h3>
                  <small className="text-muted">Core Subjects</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-warning bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-warning">
                    {filteredSubjects.filter(s => s.subjectType === 'Elective').length}
                  </h3>
                  <small className="text-muted">Elective Subjects</small>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ConfigureSubjects;
